import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Handshake,
  Building,
  Heart,
  Users,
  CheckCircle,
  Loader2,
  ArrowRight,
  Globe,
  Droplets,
  Briefcase,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = '/assets/images/about.png';

const organizationTypes = [
  'Corporate / Business',
  'Nonprofit / NGO',
  'Community Organization',
  'Government Agency',
  'Educational Institution',
  'Foundation',
  'Faith-Based Organization',
  'Healthcare Provider',
  'Individual Philanthropist',
  'Other',
];

const INITIATIVE_CONFIG = {
  hpc: {
    key: 'hpc',
    title: 'His Presence Chapel Inc.',
    shortTitle: 'HPC',
    heroTitle: 'Partner With His Presence Chapel Inc.',
    heroText:
      'Build meaningful partnerships that strengthen communities, expand outreach, and support sustainable impact through a centralized nonprofit ecosystem.',
    badge: 'Partnership Inquiry',
    primaryColor: '#F15A24',
    primaryHover: '#d64d1e',
    accentBg: 'bg-[#F15A24]/10',
    accentText: 'text-[#F15A24]',
    formIntro:
      'Complete this form to explore partnership opportunities with His Presence Chapel Inc. and its ecosystem initiatives.',
    benefitsTitle: 'Why Partner With HPC?',
    benefitsText:
      'His Presence Chapel Inc. provides governance, accountability, and strategic coordination for ecosystem-wide community impact.',
    contactEmail: 'info@hispresencechapel.org',
    contactPhone: '+1 832 887 8254',
    partnershipInterests: [
      'Program Collaboration',
      'Funding / Sponsorship',
      'Volunteer Support',
      'Faith & Community Outreach',
      'Technical Assistance',
      'Resource Sharing',
      'Awareness / Advocacy',
      'Multiple Areas',
      'Other',
    ],
    opportunityCards: [
      {
        icon: Heart,
        title: 'Shared Mission',
        description:
          'Partner around humanitarian service, empowerment, and community transformation.',
      },
      {
        icon: Users,
        title: 'Expanded Reach',
        description:
          'Work across a broader ecosystem of initiatives serving different community needs.',
      },
      {
        icon: Building,
        title: 'Strong Governance',
        description:
          'Operate through a centralized structure designed for accountability and long-term sustainability.',
      },
    ],
  },

  seeal: {
    key: 'seeal',
    title: 'SEEAL Africa',
    shortTitle: 'SEEAL Africa',
    heroTitle: 'Partner With SEEAL Africa',
    heroText:
      'Join us in creating sustainable impact across Africa through skills development, enterprise support, mentorship, and opportunity creation.',
    badge: 'SEEAL Partnership',
    primaryColor: '#0B5FA5',
    primaryHover: '#094d85',
    accentBg: 'bg-[#0B5FA5]/10',
    accentText: 'text-[#0B5FA5]',
    formIntro:
      'Complete this form to discuss collaboration opportunities with SEEAL Africa. Your submission will be centrally managed by His Presence Chapel Inc.',
    benefitsTitle: 'Partnership Opportunities',
    benefitsText:
      'SEEAL Africa collaborates with organizations, businesses, institutions, and experts committed to empowerment and economic opportunity.',
    contactEmail: 'partner@seeal.africa',
    contactPhone: '+1 832 887 8254',
    partnershipInterests: [
      'Program Sponsorship',
      'Skills Training Partnership',
      'Mentorship Program',
      'Enterprise Development Support',
      'Financial Support',
      'In-Kind Donations',
      'Technical Expertise',
      'Volunteer Support',
      'Other',
    ],
    opportunityCards: [
      {
        icon: Handshake,
        title: 'Program Sponsorship',
        description:
          'Support cohort programs, learning initiatives, and practical empowerment pathways.',
      },
      {
        icon: Briefcase,
        title: 'Enterprise Development',
        description:
          'Collaborate on entrepreneurship, employability, and enterprise growth initiatives.',
      },
      {
        icon: Globe,
        title: 'Technical Expertise',
        description:
          'Contribute mentorship, advisory support, systems, or institutional capacity building.',
      },
    ],
  },

  rahvai: {
    key: 'rahvai',
    title: 'Rehabilitation and Hope for the Vulnerable in Africa Initiative',
    shortTitle: 'RAHVAI',
    heroTitle: 'Partner With RAHVAI',
    heroText:
      'Collaborate with us to support vulnerable communities across Africa through humanitarian relief, water access, empowerment, and community development.',
    badge: 'RAHVAI Partnership',
    primaryColor: '#2D3E55',
    primaryHover: '#1a2634',
    accentBg: 'bg-[#2D3E55]/10',
    accentText: 'text-[#2D3E55]',
    formIntro:
      'Complete this partnership form to explore collaboration with RAHVAI. Your inquiry will be centrally received and managed through His Presence Chapel Inc.',
    benefitsTitle: 'Why Partner With RAHVAI?',
    benefitsText:
      'RAHVAI works with communities, local leaders, volunteers, and organizations to restore hope and strengthen resilience among vulnerable populations.',
    contactEmail: 'info@rahvai.org',
    contactPhone: '+1 832 887 8254',
    partnershipInterests: [
      'Humanitarian Relief Support',
      'Water Access Initiatives',
      'Community Development',
      'Youth Empowerment',
      'Women Empowerment',
      'Funding / Donations',
      'Volunteer Support',
      'Awareness / Advocacy',
      'Multiple Areas',
      'Other',
    ],
    opportunityCards: [
      {
        icon: Heart,
        title: 'Humanitarian Relief',
        description:
          'Support outreach initiatives that respond to urgent community needs with dignity and compassion.',
      },
      {
        icon: Droplets,
        title: 'Water Access',
        description:
          'Help expand access to clean water through community infrastructure and support programs.',
      },
      {
        icon: Users,
        title: 'Community Empowerment',
        description:
          'Collaborate on programs that create opportunity for youth, women, and underserved groups.',
      },
    ],
  },
};

export default function PartnershipPage() {
  const [searchParams] = useSearchParams();

  const initiativeParam = (searchParams.get('initiative') || 'hpc').toLowerCase();
  const initiative =
    INITIATIVE_CONFIG[initiativeParam] || INITIATIVE_CONFIG.hpc;

  const [formData, setFormData] = useState({
    initiative: initiative.key,
    initiative_name: initiative.shortTitle,
    organization_name: '',
    contact_person: '',
    email: '',
    phone: '',
    country: '',
    organization_type: '',
    partnership_interest: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submitButtonStyle = useMemo(
    () => ({
      backgroundColor: initiative.primaryColor,
      color: '#ffffff',
    }),
    [initiative.primaryColor]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      initiative: initiative.key,
      initiative_name: initiative.shortTitle,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      initiative: initiative.key,
      initiative_name: initiative.shortTitle,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.organization_name.trim()) {
      toast.error('Organization name is required.');
      return false;
    }
    if (!formData.contact_person.trim()) {
      toast.error('Contact person is required.');
      return false;
    }
    if (!formData.email.trim()) {
      toast.error('Email address is required.');
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      toast.error('Enter a valid email address.');
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error('Phone number is required.');
      return false;
    }
    if (formData.phone.trim().length < 7) {
      toast.error('Enter a valid phone number.');
      return false;
    }
    if (!formData.country.trim()) {
      toast.error('Country is required.');
      return false;
    }
    if (!formData.organization_type) {
      toast.error('Please select an organization type.');
      return false;
    }
    if (!formData.partnership_interest) {
      toast.error('Please select a partnership interest.');
      return false;
    }
    if (!formData.message.trim()) {
      toast.error('Please enter your message.');
      return false;
    }
    if (formData.message.trim().length < 20) {
      toast.error('Please provide more details in your message.');
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setFormData({
      initiative: initiative.key,
      initiative_name: initiative.shortTitle,
      organization_name: '',
      contact_person: '',
      email: '',
      phone: '',
      country: '',
      organization_type: '',
      partnership_interest: '',
      message: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/partnerships`, {
        ...formData,
        source_site: initiative.key,
        source_label: initiative.shortTitle,
      });

      setSubmitted(true);
      toast.success(
        `Your ${initiative.shortTitle} partnership inquiry has been submitted successfully.`
      );
      resetForm();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Failed to submit partnership inquiry. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        data-testid="partnership-hero"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#2D3E55]/85 to-[#1a2634]/85"></div>

        <div className="container-custom relative z-10">
          <motion.div
            className="text-center text-white max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 bg-white/10"
            >
              <Handshake size={16} />
              {initiative.badge}
            </span>

            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              {initiative.heroTitle}
            </h1>

            <p className="font-body text-lg text-white/80">
              {initiative.heroText}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Opportunity / Benefits */}
      <section className="section-padding" data-testid="partnership-benefits">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-4">
              {initiative.benefitsTitle}
            </h2>
            <p className="font-body text-lg text-slate-600">
              {initiative.benefitsText}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {initiative.opportunityCards.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full text-center hover:shadow-lg transition-shadow">
                  <CardContent className="p-8">
                    <div
                      className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6"
                      style={{ backgroundColor: `${initiative.primaryColor}15` }}
                    >
                      <item.icon
                        size={32}
                        style={{ color: initiative.primaryColor }}
                      />
                    </div>

                    <h3 className="font-heading text-xl font-semibold text-[#2D3E55] mb-3">
                      {item.title}
                    </h3>

                    <p className="font-body text-slate-600">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Form */}
      <section
        className="section-padding bg-[#F8FAFC]"
        data-testid="partnership-form-section"
      >
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-body text-sm font-medium mb-4 ${initiative.accentBg} ${initiative.accentText}`}
              >
                <Handshake size={16} />
                Centralized Partnership Form
              </span>

              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-4">
                Let’s Work Together
              </h2>

              <p className="font-body text-lg text-slate-600 mb-8">
                {initiative.formIntro}
              </p>

              <Card className="mb-6">
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-[#2D3E55] mb-4">
                    Request Destination
                  </h3>

                  <div className="space-y-3 text-slate-600 font-body">
                    <p>
                      <strong>Initiative:</strong> {initiative.title}
                    </p>
                    <p>
                      <strong>Managed Through:</strong> His Presence Chapel Inc.
                    </p>
                    <p>
                      <strong>Email:</strong>{' '}
                      <a
                        href={`mailto:${initiative.contactEmail}`}
                        className="hover:underline"
                        style={{ color: initiative.primaryColor }}
                      >
                        {initiative.contactEmail}
                      </a>
                    </p>
                    <p>
                      <strong>Phone:</strong>{' '}
                      <a
                        href={`tel:${initiative.contactPhone.replace(/\s+/g, '')}`}
                        className="hover:underline"
                        style={{ color: initiative.primaryColor }}
                      >
                        {initiative.contactPhone}
                      </a>
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-heading text-lg font-semibold text-[#2D3E55] mb-3">
                    Centralized System Note
                  </h4>
                  <p className="font-body text-slate-600">
                    This form is part of the unified HPC ecosystem form system.
                    Submissions are centrally managed while still being tagged to
                    the correct initiative for internal routing, follow-up, and
                    reporting.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {submitted ? (
                <Card className="shadow-lg">
                  <CardContent className="p-10 text-center">
                    <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                      <CheckCircle className="text-green-600" size={40} />
                    </div>

                    <h3 className="font-heading text-2xl font-semibold text-[#2D3E55] mb-4">
                      Thank You!
                    </h3>

                    <p className="font-body text-slate-600 mb-6">
                      Your partnership inquiry for <strong>{initiative.shortTitle}</strong>{' '}
                      has been submitted successfully. Our team will review it and
                      contact you soon.
                    </p>

                    <Button
                      onClick={() => {
                        setSubmitted(false);
                        resetForm();
                      }}
                      variant="outline"
                    >
                      Submit Another Inquiry
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="shadow-lg" data-testid="partnership-form">
                  <CardContent className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className={`rounded-xl border p-4 ${initiative.accentBg}`}>
                        <p className="text-sm font-medium text-[#2D3E55]">
                          This inquiry is being submitted for:{' '}
                          <strong>{initiative.title}</strong>
                        </p>
                      </div>

                      <input type="hidden" name="initiative" value={formData.initiative} />
                      <input
                        type="hidden"
                        name="initiative_name"
                        value={formData.initiative_name}
                      />

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="organization_name">Organization Name *</Label>
                          <Input
                            id="organization_name"
                            name="organization_name"
                            value={formData.organization_name}
                            onChange={handleChange}
                            required
                            className="mt-2"
                            data-testid="input-organization-name"
                          />
                        </div>

                        <div>
                          <Label htmlFor="contact_person">Contact Person *</Label>
                          <Input
                            id="contact_person"
                            name="contact_person"
                            value={formData.contact_person}
                            onChange={handleChange}
                            required
                            className="mt-2"
                            data-testid="input-contact-person"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="mt-2"
                            data-testid="input-email"
                          />
                        </div>

                        <div>
                          <Label htmlFor="phone">Phone Number *</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="mt-2"
                            data-testid="input-phone"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="country">Country *</Label>
                          <Input
                            id="country"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            required
                            className="mt-2"
                            data-testid="input-country"
                          />
                        </div>

                        <div>
                          <Label>Type of Organization *</Label>
                          <Select
                            value={formData.organization_type}
                            onValueChange={(value) =>
                              handleSelectChange('organization_type', value)
                            }
                          >
                            <SelectTrigger className="mt-2" data-testid="select-org-type">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {organizationTypes.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label>Partnership Interest *</Label>
                        <Select
                          value={formData.partnership_interest}
                          onValueChange={(value) =>
                            handleSelectChange('partnership_interest', value)
                          }
                        >
                          <SelectTrigger
                            className="mt-2"
                            data-testid="select-partnership-interest"
                          >
                            <SelectValue placeholder="Select area of interest" />
                          </SelectTrigger>
                          <SelectContent>
                            {initiative.partnershipInterests.map((interest) => (
                              <SelectItem key={interest} value={interest}>
                                {interest}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={6}
                          className="mt-2"
                          placeholder={`Tell us about your organization and how you'd like to partner with ${initiative.shortTitle}...`}
                          data-testid="textarea-message"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full text-white"
                        style={submitButtonStyle}
                        data-testid="submit-partnership-btn"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit Partnership Inquiry
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}